// ./src/pages/private/EventPageEditorPage/BlockErrorBoundary.tsx
//
// A tiny error boundary so a single malformed block (bad HTML, weird embed, odd
// config) renders a friendly placeholder in the preview instead of taking the
// whole editor down. Wrap each BlockPreview in one.

import React from 'react';
import { Box, Text, Group } from '@mantine/core';

import { AppIcon } from '@em/ui';

interface Props {
  children: React.ReactNode;
  label?: string;
}

interface State {
  hasError: boolean;
}

export class BlockErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  override componentDidUpdate(prev: Props) {
    // Reset when the wrapped content changes (e.g. the block was edited/fixed).
    if (prev.children !== this.props.children && this.state.hasError) {
      this.setState({ hasError: false });
    }
  }

  override render() {
    if (this.state.hasError) {
      return (
        <Box style={{ border: '1px dashed var(--mantine-color-red-4)', borderRadius: 8, padding: 16, background: 'var(--mantine-color-red-0)' }}>
          <Group gap="xs" c="red.7">
            <AppIcon iconName="AlertTriangle" size={18} />
            <Text size="md" fw={600}>
              {this.props.label ?? 'This block could not be displayed'}
            </Text>
          </Group>
          <Text size="xs" c="dimmed" mt={4}>
            Its content may be invalid — edit the block to fix it.
          </Text>
        </Box>
      );
    }
    return this.props.children;
  }
}
